import { ReactNode, useEffect, useState } from 'react'
import { useRouter } from 'next/router'
import { createNavigationMenuItems } from 'src/components/navigation/nav-items'
import { useMerchant } from 'src/lib/contexts/MerchantContext'
import { hasAuthCookie, hasRefreshCookie, setVersionCookie, refresh } from 'src/lib/utils/helpers'
import { useFilter } from 'src/lib/contexts/FilterContext'
import { RouteGuard } from './navigation/RouteGuard'
import { constants } from 'src/lib/utils/constants'
import { refreshExpiredIdToken } from 'src/lib/utils/auth'
import Cookies from 'js-cookie'
import { useUser } from 'src/lib/contexts/UserContext'
import { useOrganisationContext } from 'src/lib/contexts/OrganisationContext'
import { getXShopfloVersion, acceptOrganizationAgreement } from 'src/lib/core/api-methods'
import { useLocale } from 'src/lib/hooks/useLocal'
import MainLayout from './MainLayout'
import { inPreOnboarding } from 'src/lib/utils/OrganisationHelpers'
import useNavigation, { JourneyStep } from 'src/lib/hooks/useNavigation'
import { useNavigationRBAC } from 'src/lib/utils/navigation-rbac'
import ThirdPartyInitializer from 'src/components/common/initializers/ThirdPartyInitializer'
import { OnboardingStep } from 'src/lib/types/onboardingV2'
import TermsAndConditionsDialogV2 from 'src/components/common/TermsAndConditionsDialogV2'

const TNC_PDF_URL = 'https://documents.shopflo.com/Shopflo_Merchant_Agreement.pdf'

interface Props {
  children: ReactNode
}

const UserLayoutV2 = ({ children }: Props) => {
  const {
    getters: { merchant }
  } = useMerchant()
  const router = useRouter()
  const { setFiltersCtx } = useFilter()
  const { user, isUserConfigured, mutateUser } = useUser()
  const { t } = useLocale()

  const {
    getters: { organisationData, billingPopUp, isShopConnected },
    actions: { refetchOrganisation }
  } = useOrganisationContext()

  const { filterItems, checkRedirect, getJourneyStep } = useNavigationRBAC()
  const {
    actions: { setJourneyStep }
  } = useNavigation()

  const [isTnCDialogOpen, setIsTnCDialogOpen] = useState(false)
  const [hasTnCDialogBeenShown, setHasTnCDialogBeenShown] = useState(
    () => Cookies.get(constants.FLO_TNC_DIALOG_SHOWN) === 'true'
  )

  const checkForRefreshToken = async () => {
    if (hasRefreshCookie()) {
      await refreshExpiredIdToken()
      onInit()
    } else {
      if (router.asPath?.includes('auth')) return
      router.replace('/auth/login')
    }
  }

  useEffect(() => {
    if (hasAuthCookie()) {
      onInit()
      return
    }

    checkForRefreshToken()
  }, [])

  const onInit = async () => {
    const versionResponse: any = await getXShopfloVersion()
    if (versionResponse?.data?.success) {
      if (Cookies.get(constants.FLO_SHOPFLO_VERSION) !== versionResponse?.data?.data?.version) {
        setVersionCookie(versionResponse?.data?.data?.version ?? 'stable')
        refresh()
      }
    } else {
      setVersionCookie('stable')
    }
    refetchOrganisation()
    mutateUser()
  }

  const handleTnCAccept = async () => {
    await acceptOrganizationAgreement(organisationData.id)
    refetchOrganisation()
  }

  useEffect(() => {
    if (!user || !organisationData?.id) return

    // Show T&C dialog once per session if agreement is pending, user is admin and not super admin
    if (organisationData.organizationAgreement?.status === 'PENDING' && user.isAdmin && !user.isSuperAdmin && !hasTnCDialogBeenShown) {
      setIsTnCDialogOpen(true)
      setHasTnCDialogBeenShown(true)
    }

    const currPath = router.pathname
    setFiltersCtx()

    if (!isUserConfigured && router.asPath !== '/user/profile') {
      const shouldGoToProfile =
        isShopConnected || organisationData.onboardingState.current_state !== OnboardingStep.user_profile

      if(shouldGoToProfile){
        router.push('/user/profile')
        return
      }
    }

    if (!isShopConnected && inPreOnboarding(organisationData)) {
      router.replace('/onboarding')
      return
    }

    if (billingPopUp.hasOverdueInvoices && !user?.isSuperAdmin) {
      router.replace('/billing')
      return
    }

    const journeyStep = getJourneyStep(currPath)
    if (journeyStep !== JourneyStep.NONE) {
      setJourneyStep(journeyStep)
      return
    }

    const allNavItems = createNavigationMenuItems(
      t,
      isShopConnected,
      isUserConfigured,
      billingPopUp.hasOverdueInvoices && !user?.isSuperAdmin
    )

    if (!allNavItems.length) return

    const accessibleNavItems = filterItems(allNavItems, true)
    const browserPath = typeof window !== 'undefined' ? window.location.pathname : currPath
    if (browserPath.startsWith('/apps-integrations')) return

    const { shouldRedirect, redirectPath } = checkRedirect(currPath, accessibleNavItems)

    if (shouldRedirect) {
      router.replace(redirectPath)
      return
    }
  }, [user, organisationData, isShopConnected, isUserConfigured, billingPopUp, t, router.pathname, hasTnCDialogBeenShown])

  return (
    <>
      <MainLayout>
        <RouteGuard>{children}</RouteGuard>
      </MainLayout>
      <ThirdPartyInitializer />
      <TermsAndConditionsDialogV2
        isOpen={isTnCDialogOpen}
        onClose={() => {
          const midnight = new Date()
          midnight.setHours(23, 59, 59, 999)
          Cookies.set(constants.FLO_TNC_DIALOG_SHOWN, 'true', { expires: midnight })
          setIsTnCDialogOpen(false)
        }}
        pdfUrl={TNC_PDF_URL}
        onAccept={handleTnCAccept}
      />
    </>
  )
}

export default UserLayoutV2
